import React, { useRef, useState } from "react";
import { Button } from "../Button/Loadable";
import { error, success } from "../Notify";
import httpclient from "libs/Axios";
import { useAuth } from "core/context/AuthContext/AuthContextProvider";
import debounce from "lodash/debounce";
import axios, { AxiosError } from "axios";
import { ifNotPreviousYear, matchesPatternStartsWith } from "core/utils/constants";
import { useParams } from "react-router-dom";
import { getRouteAccess } from "core/utils/accessControl";

interface Props {
    moduleURL: string;
    callBackFunction?: any;
    width?: string;
    height?: string;
    padding?: string;
    forceVisible?: boolean;
    className?: string;
}

const ImportButton = ({ moduleURL, callBackFunction, width, height, padding, forceVisible, className }: Props) => {
    const params: any = useParams();
    const importFileInputRef: any = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const authData = useAuth();
    const currentModule = authData?.PERMISSION_REGISTRY?.modules?.find(item => matchesPatternStartsWith(window.location.pathname, item?.path))?.key;
    const isUpsertAllowed = currentModule ? getRouteAccess(authData, currentModule, params?.route)?.upsert : false;

    // Debounced file upload handler with increased debounce time
    const debouncedHandleImport = debounce(async (selectedFile) => {
        if (selectedFile) {
            setIsUploading(true);

            // Show processing toast - using only the two supported arguments
            success("Processing your import, please wait...", true);

            const formData = new FormData();
            formData.append('file', selectedFile[0]);

            const hasInstituteId = ["staff", "parent", 'student']?.includes(moduleURL)
                ? `&institute_id=${authData?.INSTITUTE_ID}`
                : '';

            const hasAcademicYeadId = ["classTimetable", "branchHoliday", 'student', 'feeSchedule', 'feeClassCategory', 'examStudentResult']?.includes(moduleURL)
                ? `&academic_year_id=${authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_ID}`
                : '';

            try {
                const response = await httpclient().post(
                    `/data/${moduleURL}/bulkSave?${["classSubject"]?.includes(moduleURL) ? 'BRANCH_ID' : 'branch_id'}=${authData?.BRANCH_ID}${hasAcademicYeadId}${hasInstituteId}`,
                    formData, {
                    headers: {
                        'Custom-Header': 'multipart/form-data',
                    },
                }
                );

                if (response) {
                    if (response?.data?.error?.length > 0) {
                        const errorMessages = response?.data?.error?.map((error: any) => {
                            return `${error?.field || ""} (${error?.value || ""}): ${error?.cause || ""}`;
                        }).join(' ');
                        error(errorMessages, true);
                    }
                    else {
                        success('Import completed successfully!', true);
                    }
                    if (!!callBackFunction) {
                        callBackFunction();
                    }
                }
                else {
                    error('Import failed. Please try again.', true);
                }
            } catch (e: unknown) {
                console.log('error', e);
                console.error('Axios error:', e);

                // Type guard to check if the error is an AxiosError
                if (axios.isAxiosError(e)) {
                    const axiosError = e as AxiosError;

                    if (axiosError.response) {
                        // Get the error message from the response if available
                        const responseData = axiosError.response.data as any;
                        const backendErrorMessage = responseData?.message ||
                            responseData?.error ||
                            responseData?.errorMessage ||
                            responseData;

                        // If we have a proper error message from backend, use it
                        if (backendErrorMessage && typeof backendErrorMessage === 'string') {
                            error(`Import failed: ${backendErrorMessage}`, true);
                        }
                        // If the error is an object, try to extract a message
                        else if (backendErrorMessage && typeof backendErrorMessage === 'object') {
                            // Try to get a message from the error object
                            const extractedMessage = backendErrorMessage.message ||
                                (typeof backendErrorMessage === 'object' && Object.values(backendErrorMessage).join(', '));

                            error(`Import failed: ${extractedMessage || 'Server error occurred'}`, true);
                        }
                        // Fallback to status code based messages if no clear message is found
                        else {
                            const status = axiosError.response.status;
                            switch (status) {
                                case 400:
                                    error('Import failed: Bad request. Please check your file format.', true);
                                    break;
                                case 401:
                                    error('Import failed: Authentication required. Please log in again.', true);
                                    break;
                                case 403:
                                    error('Import failed: You do not have permission to import this data.', true);
                                    break;
                                case 404:
                                    error('Import failed: The requested resource was not found.', true);
                                    break;
                                case 413:
                                    error('Import failed: File size exceeds the maximum limit.', true);
                                    break;
                                case 422:
                                    error('Import failed: The provided data could not be processed. Please check your file format.', true);
                                    break;
                                case 500:
                                    error('Import failed: Server error. Please try again later.', true);
                                    break;
                                default:
                                    error(`Import failed: Server returned status code ${status}.`, true);
                            }
                        }
                    } else if (axiosError.request) {
                        // The request was made but no response was received
                        error('Import failed: No response from server. Please check your internet connection.', true);
                    } else {
                        // Something happened in setting up the request that triggered an error
                        error(`Import failed: ${axiosError.message || 'Unknown error occurred'}`, true);
                    }
                } else {
                    // For non-Axios errors
                    const err = e as Error;
                    error(`Import failed: ${err.message || 'Unknown error occurred'}`, true);
                }
            }

            importFileInputRef.current.value = '';
            setIsUploading(false);
        } else {
            error('Please select a valid .xlsx file.', true);
        }
    }, 500); // Increased from 300ms to 500ms debounce time

    const handleImportFileEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = importFileInputRef.current.files;
        debouncedHandleImport(selectedFile);
    };

    const fireImportInput = () => {
        importFileInputRef?.current?.click();
    };

    return (
        (forceVisible || (ifNotPreviousYear(authData) && isUpsertAllowed)) ? <div>
            <input
                type="file"
                accept=".xlsx"
                ref={importFileInputRef}
                className="hidden"
                onChange={handleImportFileEvent}
            />
            <Button
                button
                height={height ? height : `37px`}
                type="primary"
                label={isUploading ? "Importing..." : "Import"}
                width={width && width}
                padding={padding && padding}
                className={className}
                onClick={() => fireImportInput()}
                disabled={isUploading}
            />
        </div> : <></>
    );
}

export default ImportButton;
